/**
 * Focused per-carton workspace at
 * `/shipping/pre-pack/shipments/:shipmentId/cartons/:cartonNumber`.
 *
 * Full-width, single purpose: pack ONE carton. The queue is one route up
 * (`/shipping/pre-pack`). Deep links work because the page loads the carton
 * DTO from `GET /api/prepack/shipments/:id/cartons/:carton` (409 when not pack-ready).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useNavigationTrail } from "@/lib/navigationTrailContext";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Lock,
  Package,
  Printer,
  ScanLine,
  Trash2,
  Unlock,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/Button";
import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { ZplPrinterListStatus } from "@/components/ZplPrinterSelect";
import { Card, CardBody } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { EntityPeekHint } from "@/components/hub/EntityPeekHint";
import { CardTitleWithHelp, HoverHintContent, HoverHintWithGlyph, PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { playPairFailureSound, playPairSuccessSound } from "@/lib/floorStationPairAudio";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import {
  FLOOR_SCAN_INPUT_FIELD_PROPS,
  floorSerialInputHasScannerTerminator,
  handleFloorSerialEnterKey,
  normalizeFloorSerialInput,
  readFloorSerialFromInput,
} from "@/lib/floorSerialScan";
import { laborRemotePersistenceDisabled } from "@/lib/labor/laborEnv";
import { isLaborOwnerDeparting } from "@/lib/labor/laborOwnerDeparture";
import { prePackLaborPageKeyForCarton, formatLaborLocationLabel } from "@/lib/labor/laborPageKey";
import { flashAndBumpToOverview } from "@/lib/labor/laborTimeoutHandlers";
import { useLaborDevStatusPanelRegistration } from "@/lib/labor/laborDevStatusPanelContext";
import type { LaborStatusPanelProps } from "@/lib/labor/LaborStatusPanel";
import { useLaborSession } from "@/lib/labor/useLaborSession";
import { useAuth } from "@/components/AuthProvider";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

import { formatPackQty } from "./formatPackQty";
import { PrePackCartonBatchLink, PrePackCartonShipmentTitle } from "./prepackCartonEntityLinks";
import {
  formatKitSerialBreakdown,
  packedPhysicalSerials,
  pendingPhysicalSerials,
  plannedPhysicalSerials,
} from "./prepackPhysicalDevices";
import { PrePackCartonOperatorStack } from "./PrePackCartonOperatorStack";
import { PrePackCartonTargetModal } from "./PrePackCartonOverrideCard";
import { buildPrePackLaborAllocationTableRows, buildPrePackLaborStationHoverAriaLabel } from "./prepackLaborDevHint";
import { PrePackLaborStationHoverTable } from "./prepackLaborStationHoverTable";
import { QUEUE_CARTONS_KEY, QUEUE_KEY } from "./PrePackQueuePage";
import { PrePackSealCartonModal } from "./PrePackSealCartonModal";
import { cartonOverTargetCount, formatSealSuccessToast, showSealCartonButton } from "./prepackSealCarton";
import {
  cartonPath,
  nextCartonAfter,
  previousCartonBefore,
} from "./prepackQueueNavigation";
import {
  fetchCartonSerials,
  fetchCartonUnserialized,
  fetchPrePackCarton,
  fetchPrePackCartonPackOutComponents,
  fetchPrePackQueue,
  fetchPrePackActiveCartonOperators,
  fetchSealCartonPreview,
  formatShipDate,
  packUnserialized,
  printCartonLabel,
  removeBoxLabel,
  reprintBoxLabel,
  saveBoxLabel,
  sealPrepackCarton,
  unpackUnserialized,
  unsealPrepackCarton,
  prePackActiveCartonOperatorsQueryKey,
  type CartonItem,
  type CartonRow,
  type CartonSerialRow,
  type CartonUnserializedItem,
  type PackOutComponent,
  type PackOutComponentsResponse,
  type SealCartonPreviewResponse,
} from "./prepackApi";
import { PREPACK_LABOR_FLASH_STORAGE_KEY } from "./prepackLaborFlash";
import { usePrepackZplPrinter } from "./prepackZplPrinter";

const PREPACK_PRINT_BOX_LABEL_STORAGE_KEY = "prepack-print-box-label";

const PACK_CARTON_SECTION_HOVER_HINT =
  "Carton label ZPL is composed on this server from the carton plan and sent to the print relay using the selected Windows ZPL printer. ABB SKUs must have carton and box labels assigned on Batch details before they can be packed; this button then sends that ABB carton label. When you use Print box label (skip box scan) under Serialized devices, the server saves the device serial as the box serial, registers it in label_printed_serial (rejecting duplicates), then prints the assigned ABB PDD box label. Non-ABB SKUs print the hub box label (FI-3C, FI-5A, or FI-5A-W). Printed box mode needs PRINT_SERVER_URL on the API host.";

const SERIALIZED_DEVICES_SECTION_HOVER_HINT =
  "Scan the device label and a physical box label when kitted stock is still on hand at pre-pack; otherwise turn on Print box label (skip box scan) to print on demand. ABB SKUs cannot be packed until carton and box labels are assigned on Batch details; print mode then prints that ABB box label. Non-ABB SKUs print the hub box template. If print fails after save, the pairing stays on the table: use Reprint on that row (do not remove or rescan the same unit). Removing the pairing also removes the label_printed_serial row when print mode created it. Use the row remove control only when you need to undo a pairing.";

const cartonKey = (shipmentId: number, cartonNumber: number) =>
  ["prepack", "carton", shipmentId, cartonNumber] as const;
const cartonSerialsKey = (shipmentId: number, cartonNumber: number) =>
  ["prepack", "carton-serials", shipmentId, cartonNumber] as const;
const cartonUnserializedKey = (shipmentId: number, cartonNumber: number) =>
  ["prepack", "carton-unserialized", shipmentId, cartonNumber] as const;
const cartonPackOutComponentsKey = (shipmentId: number, cartonNumber: number) =>
  ["prepack", "carton-pack-out-components", shipmentId, cartonNumber] as const;

const PACK_OUT_ACCESSORIES_HOVER_HINT =
  "Accessories are Pack Out slots on the BOM (kit_to_station_id on template initial). Component SKUs and per-unit quantities come from the same rule-resolved BOM as BOM preview (create_bom_specific), not the unresolved initial placeholders. Total is per unit times planned units on this carton. If a SKU has no BOM template, accessories cannot be listed here—packing is not blocked.";

const CARTON_PLAN_KIT_SKU_HOVER_HINT =
  "Kit SKU rows (for example three-phase) are whole kits only. Leftover serialized devices that cannot form a complete kit are not planned and cannot ship. Finish the missing serials or move leftovers on Manufacturing Alternates. You never split one kit across two cartons.";

export function PrePackCartonPage() {
  const params = useParams<{ shipmentId: string; cartonNumber: string }>();
  const shipmentId = Number.parseInt(params.shipmentId ?? "", 10);
  const cartonNumber = Number.parseInt(params.cartonNumber ?? "", 10);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const bumpPrePackLaborToQueueWithFlash = useCallback(
    (message: string) => {
      flashAndBumpToOverview({
        navigate,
        to: "/shipping/pre-pack",
        storageKey: PREPACK_LABOR_FLASH_STORAGE_KEY,
        message,
        replace: true,
      });
    },
    [navigate],
  );
  const laborPage = useMemo(
    () =>
      Number.isFinite(shipmentId) && shipmentId > 0 && Number.isFinite(cartonNumber) && cartonNumber > 0
        ? prePackLaborPageKeyForCarton(shipmentId, cartonNumber)
        : "",
    [shipmentId, cartonNumber],
  );
  const labor = useLaborSession({
    page: laborPage,
    domain: "shipping",
    moduleGate: "shipping",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled("prepack"),
    workLabel: "Pre-Pack",
    autoStart: laborPage !== "",
    requirePersistedClockIn: true,
    onIdleTimeout: () =>
      bumpPrePackLaborToQueueWithFlash(
        "Clocked out due to inactivity on this carton. Open the carton again from the queue when you return.",
      ),
    onPersistedSessionLost: () =>
      bumpPrePackLaborToQueueWithFlash(
        "Your hub labor session for this carton ended (timeout, error, or the session was closed on the server). Use the Pre-Pack queue breadcrumb to return, then open the carton again to clock back in.",
      ),
    onSessionEndedRemotely: () =>
      bumpPrePackLaborToQueueWithFlash(
        "Clocked out from another tab or the session was closed on the server. Open the carton again from the queue when you want to continue.",
      ),
    onStopped: () =>
      bumpPrePackLaborToQueueWithFlash(
        "Clocked out. Open the carton again from the queue when you want to continue.",
      ),
  });
  const laborGateReady = laborPage !== "" && labor.gateReady;

  const laborGateConflictBumpRef = useRef(false);
  useEffect(() => {
    if (!Number.isFinite(shipmentId) || shipmentId <= 0 || !Number.isFinite(cartonNumber) || cartonNumber <= 0) {
      return;
    }
    if (labor.loading) return;
    if (isLaborOwnerDeparting()) return;
    if (labor.activeOnOtherPage) {
      if (!laborGateConflictBumpRef.current) {
        laborGateConflictBumpRef.current = true;
        bumpPrePackLaborToQueueWithFlash(
          `Finish clocking out of ${formatLaborLocationLabel(labor.activeOnOtherPage)} before opening this carton. Return to the queue, stop that session, then try again.`,
        );
      }
      return;
    }
    laborGateConflictBumpRef.current = false;
  }, [shipmentId, cartonNumber, labor.loading, labor.activeOnOtherPage, bumpPrePackLaborToQueueWithFlash]);

  const laborGateFailureBumpRef = useRef(false);
  useEffect(() => {
    if (labor.gateReady) {
      laborGateFailureBumpRef.current = false;
    }
  }, [labor.gateReady]);

  useEffect(() => {
    if (!Number.isFinite(shipmentId) || shipmentId <= 0 || !Number.isFinite(cartonNumber) || cartonNumber <= 0) {
      return;
    }
    if (labor.loading || labor.gateReady) return;
    if (labor.activeOnOtherPage) return;
    if (!labor.banner?.message) return;
    if (laborGateFailureBumpRef.current) return;
    laborGateFailureBumpRef.current = true;
    bumpPrePackLaborToQueueWithFlash(labor.banner.message);
  }, [
    shipmentId,
    cartonNumber,
    labor.loading,
    labor.gateReady,
    labor.activeOnOtherPage,
    labor.banner?.message,
    bumpPrePackLaborToQueueWithFlash,
  ]);

  const cartonSerialsLaborHintQuery = useQuery({
    queryKey: cartonSerialsKey(shipmentId, cartonNumber),
    queryFn: () => fetchCartonSerials(shipmentId, cartonNumber),
    enabled:
      import.meta.env.DEV &&
      laborPage !== "" &&
      Number.isFinite(shipmentId) &&
      shipmentId > 0 &&
      Number.isFinite(cartonNumber) &&
      cartonNumber > 0,
    staleTime: 10_000,
  });

  const prePackLaborStationHover = useMemo(() => {
    const serials = cartonSerialsLaborHintQuery.data ?? [];
    const rows = buildPrePackLaborAllocationTableRows(labor.elapsedSeconds, serials);
    const hoverHintAriaLabel = buildPrePackLaborStationHoverAriaLabel(labor.elapsedSeconds, rows, labor.scans);
    return {
      hoverHintContent: (
        <PrePackLaborStationHoverTable grossElapsedSeconds={labor.elapsedSeconds} rows={rows} />
      ),
      hoverHintAriaLabel,
    };
  }, [cartonSerialsLaborHintQuery.data, labor.elapsedSeconds, labor.scans]);

  const prePackLaborDevPanelSnapshot = useMemo((): LaborStatusPanelProps => {
    return {
      isWorking: labor.isWorking,
      elapsedSeconds: labor.elapsedSeconds,
      workLabel: "Pre-Pack",
      banner: labor.banner,
      lastSummary: labor.lastSummary,
      heartbeatIntervalMs: labor.heartbeatIntervalMs,
      idleTimeoutMs: labor.idleTimeoutMs,
      devSessionContextRow: {
        labelText: "Station: pre-pack",
        ...prePackLaborStationHover,
      },
    };
  }, [
    labor.isWorking,
    labor.elapsedSeconds,
    labor.banner,
    labor.lastSummary,
    labor.heartbeatIntervalMs,
    labor.idleTimeoutMs,
    prePackLaborStationHover,
  ]);

  useLaborDevStatusPanelRegistration(
    import.meta.env.DEV && laborPage !== "",
    prePackLaborDevPanelSnapshot,
  );

  const touchActivity = (): void => {
    labor.touchActivity();
  };

  const cartonQuery = useQuery({
    queryKey: cartonKey(shipmentId, cartonNumber),
    queryFn: () => fetchPrePackCarton(shipmentId, cartonNumber),
    enabled: Number.isFinite(shipmentId) && shipmentId > 0 && Number.isFinite(cartonNumber) && cartonNumber > 0,
    staleTime: 10_000,
  });

  /** Active work queue only (same ordering as the queue page "Next"). */
  const workQueueQuery = useQuery({
    queryKey: [...QUEUE_CARTONS_KEY, false] as const,
    queryFn: () => fetchPrePackQueue(false).then((response) => response.cartons),
    staleTime: 15_000,
  });

  function refreshAll() {
    void queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    void queryClient.invalidateQueries({ queryKey: cartonKey(shipmentId, cartonNumber) });
    void queryClient.invalidateQueries({ queryKey: cartonSerialsKey(shipmentId, cartonNumber) });
    void queryClient.invalidateQueries({ queryKey: cartonUnserializedKey(shipmentId, cartonNumber) });
    void queryClient.invalidateQueries({
      queryKey: cartonPackOutComponentsKey(shipmentId, cartonNumber),
    });
  }

  if (!Number.isFinite(shipmentId) || shipmentId <= 0 || !Number.isFinite(cartonNumber) || cartonNumber <= 0) {
    return (
      <Card>
        <CardBody>
          <p className="py-8 text-center text-sm text-rose-700">Invalid shipment or carton number.</p>
          <p className="text-center text-sm text-slate-500">
            Use the breadcrumb to return to the Pre-Pack queue.
          </p>
        </CardBody>
      </Card>
    );
  }

  const carton = cartonQuery.data;

  return (
    <div className="space-y-4">
      {!laborGateReady && laborPage !== "" ? (
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 text-slate-600">
          <Loader2 aria-hidden className="h-5 w-5 animate-spin" />
          <p className="text-sm">Clocking in…</p>
        </div>
      ) : null}
      {laborGateReady && cartonQuery.isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading carton…
        </div>
      ) : null}
      {laborGateReady && cartonQuery.isError ? (
        <Card>
          <CardBody className="space-y-4">
            <p className="text-sm text-rose-700">
              Could not load:{" "}
              {cartonQuery.error instanceof Error
                ? cartonQuery.error.message
                : "Carton not found on the pre-pack queue."}
            </p>
            <p className="text-sm text-slate-500">Use the breadcrumb to return to the Pre-Pack queue.</p>
          </CardBody>
        </Card>
      ) : null}
      {laborGateReady && carton ? (
        <PrePackCartonLoaded
          carton={carton}
          laborSessionId={labor.session != null && labor.session.id > 0 ? labor.session.id : undefined}
          workQueueQuery={workQueueQuery}
          actionError={actionError}
          setActionError={setActionError}
          touchActivity={touchActivity}
          refreshAll={refreshAll}
        />
      ) : null}
    </div>
  );
}

interface PrePackCartonLoadedProps {
  carton: CartonRow;
  laborSessionId?: number;
  workQueueQuery: ReturnType<typeof useQuery<CartonRow[], Error>>;
  actionError: string | null;
  setActionError: React.Dispatch<React.SetStateAction<string | null>>;
  touchActivity: () => void;
  refreshAll: () => void;
}

function PrePackCartonLoaded({
  carton,
  laborSessionId,
  workQueueQuery,
  actionError,
  setActionError,
  touchActivity,
  refreshAll,
}: PrePackCartonLoadedProps) {
  const navigate = useNavigate();
  const { showToast } = useHubToast();
  const shippingWrite = useHubModuleWrite("shipping");
  const [sealModalOpen, setSealModalOpen] = useState(false);
  const [sealPreview, setSealPreview] = useState<SealCartonPreviewResponse | null>(null);
  const [sealPreviewLoading, setSealPreviewLoading] = useState(false);
  const [sealPreviewError, setSealPreviewError] = useState<string | null>(null);
  const [unsealConfirmOpen, setUnsealConfirmOpen] = useState(false);
  const { trail, navigateToTrailEntry } = useNavigationTrail();
  const { session } = useAuth();
  const viewerUserId = session?.user?.id ?? null;
  const workQueue = Array.isArray(workQueueQuery.data) ? workQueueQuery.data : [];
  const nextCartonRow = nextCartonAfter(workQueue, carton.shipmentId, carton.cartonNumber);
  const previousCartonRow = previousCartonBefore(workQueue, carton.shipmentId, carton.cartonNumber);

  const operatorsQuery = useQuery({
    queryKey: prePackActiveCartonOperatorsQueryKey,
    queryFn: fetchPrePackActiveCartonOperators,
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
  const cartonOperators = useMemo(() => {
    return (operatorsQuery.data ?? []).filter(
      (operator) =>
        operator.shipmentId === carton.shipmentId && operator.cartonNumber === carton.cartonNumber,
    );
  }, [operatorsQuery.data, carton.shipmentId, carton.cartonNumber]);

  const viewerKnown = viewerUserId !== null && viewerUserId !== "";
  const showOtherOperatorsOnCarton =
    cartonOperators.length > 0 &&
    (!viewerKnown || cartonOperators.some((operator) => operator.userId !== viewerUserId));

  function goToPrePackQueue(): void {
    if (trail.length > 0) {
      navigateToTrailEntry(0);
      return;
    }
    navigate("/shipping/pre-pack");
  }

  const sealMutation = useMutation({
    mutationFn: () => sealPrepackCarton(carton.shipmentId, carton.cartonNumber),
    onSuccess: (result) => {
      setSealModalOpen(false);
      setSealPreview(null);
      setSealPreviewError(null);
      showToast({
        tone: "success",
        message: formatSealSuccessToast(
          result.sealedCartonNumber,
          result.primaryDestinationCartonNumber,
          result.destinationCartons,
        ),
      });
      refreshAll();
      navigate(cartonPath(carton.shipmentId, result.primaryDestinationCartonNumber));
    },
    onError: (error) => {
      setSealPreviewError(error instanceof ApiError ? error.message : String(error));
    },
  });

  const unsealMutation = useMutation({
    mutationFn: () => unsealPrepackCarton(carton.shipmentId, carton.cartonNumber),
    onSuccess: () => {
      setUnsealConfirmOpen(false);
      showToast({ tone: "success", message: `Carton ${String(carton.cartonNumber)} unsealed.` });
      refreshAll();
    },
    onError: (error) => {
      setActionError(error instanceof ApiError ? error.message : String(error));
    },
  });

  useEffect(() => {
    if (!sealModalOpen) return;
    let cancelled = false;
    setSealPreviewLoading(true);
    setSealPreviewError(null);
    setSealPreview(null);
    void fetchSealCartonPreview(carton.shipmentId, carton.cartonNumber)
      .then((preview) => {
        if (!cancelled) setSealPreview(preview);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setSealPreviewError(error instanceof ApiError ? error.message : String(error));
        }
      })
      .finally(() => {
        if (!cancelled) setSealPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sealModalOpen, carton.shipmentId, carton.cartonNumber]);

  return (
    <>
      {showOtherOperatorsOnCarton || previousCartonRow ? (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {showOtherOperatorsOnCarton ? (
            <div className="flex flex-col items-end gap-0.5">
              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                {viewerKnown ? "Other operators on this carton" : "Operators on this carton"}
              </span>
              <PrePackCartonOperatorStack
                operators={cartonOperators}
                viewerUserId={viewerUserId}
                omitViewer={viewerKnown}
              />
            </div>
          ) : null}
          {previousCartonRow ? (
            <Link
              to={cartonPath(previousCartonRow.shipmentId, previousCartonRow.cartonNumber)}
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              ← Previous in queue
            </Link>
          ) : null}
        </div>
      ) : null}

      {actionError ? (
        <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{actionError}</span>
          <button
            type="button"
            className="ml-auto shrink-0 text-rose-600 hover:text-rose-800"
            onClick={() => setActionError(null)}
            aria-label="Dismiss error"
          >
            <XCircle className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : null}

      <CartonHeader carton={carton} />

      {(carton.mfrRemark || carton.soInstructions || carton.lineItemInstructions) && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {carton.mfrRemark ? (
            <p>
              <strong>Mfr note:</strong> {carton.mfrRemark}
            </p>
          ) : null}
          {carton.soInstructions ? (
            <p>
              <strong>SO instructions:</strong> {carton.soInstructions}
            </p>
          ) : null}
          {carton.lineItemInstructions ? (
            <p>
              <strong>Line note:</strong> {carton.lineItemInstructions}
            </p>
          ) : null}
        </div>
      )}

      <CartonWorkspace
        carton={carton}
        shippingWrite={shippingWrite}
        laborSessionId={laborSessionId}
        onActionError={(message) => setActionError(message)}
        onAfterChange={refreshAll}
        onActivity={touchActivity}
        onRequestSeal={() => setSealModalOpen(true)}
        onRequestUnseal={() => setUnsealConfirmOpen(true)}
      />

      <PrePackSealCartonModal
        open={sealModalOpen}
        preview={sealPreview}
        previewLoading={sealPreviewLoading}
        previewError={sealPreviewError}
        confirming={sealMutation.isPending}
        onConfirm={() => sealMutation.mutate()}
        onCancel={() => {
          if (sealMutation.isPending) return;
          setSealModalOpen(false);
          setSealPreviewError(null);
        }}
      />

      {unsealConfirmOpen ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-medium">Unseal carton {String(carton.cartonNumber)}?</p>
          <p className="mt-1">Unsealing does not move items back. You may need to correct allocations manually.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={unsealMutation.isPending}
              onClick={() => setUnsealConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              variant="primary"
              disabled={unsealMutation.isPending}
              onClick={() => unsealMutation.mutate()}
            >
              {unsealMutation.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Unlock className="mr-1.5 h-4 w-4" aria-hidden />
              )}
              Unseal carton
            </Button>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        {cartonOverTargetCount(carton) > 0 ? (
          <p className="text-sm text-rose-800">
            <span className="font-semibold">{formatPackQty(cartonOverTargetCount(carton))} over the carton target.</span>{" "}
            Packed quantity stays in the carton. Sealing stays blocked until the extra is removed with the normal pack
            controls.
          </p>
        ) : carton.isFullyPacked ? (
          <div className="text-sm text-slate-700">
            {nextCartonRow ? (
              <p>
                <span className="font-semibold text-emerald-800">Complete on the plan.</span> Next in line: Shipment{" "}
                {nextCartonRow.shipmentId} · Carton {nextCartonRow.cartonNumber}.
              </p>
            ) : (
              <p>
                <span className="font-semibold text-emerald-800">All done.</span> There is no further carton in the
                active queue.
              </p>
            )}
          </div>
        ) : (
          <span aria-hidden />
        )}
          <div className="flex flex-wrap gap-2">
            {carton.sealed === true && nextCartonRow ? (
              <Link
                to={cartonPath(nextCartonRow.shipmentId, nextCartonRow.cartonNumber)}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
              >
                Continue on carton {nextCartonRow.cartonNumber}
                <ChevronRight className="h-4 w-4" aria-hidden />
              </Link>
            ) : null}
            {nextCartonRow ? (
              carton.isFullyPacked ? (
                <Link
                  to={cartonPath(nextCartonRow.shipmentId, nextCartonRow.cartonNumber)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
                >
                  Open next carton
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </Link>
              ) : (
                <span
                  className="inline-flex h-10 cursor-not-allowed items-center justify-center gap-2 rounded-md bg-slate-200 px-4 text-sm font-medium text-slate-500"
                  title="Finish packing until every line on the plan shows zero pending"
                >
                  Open next carton
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </span>
              )
            ) : carton.isFullyPacked ? (
              <Button type="button" variant="primary" onClick={goToPrePackQueue}>
                Back to queue
                <Package className="h-4 w-4" aria-hidden />
              </Button>
            ) : null}
          </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Header — title, totals, print button
// ---------------------------------------------------------------------------

function CartonHeader({ carton }: { carton: CartonRow }) {
  const totalDevicesPlanned = carton.items.reduce((sum, item) => sum + plannedPhysicalSerials(item), 0);
  const totalDevicesPacked = carton.items.reduce((sum, item) => sum + packedPhysicalSerials(item), 0);
  const totalDevicesPending = carton.items.reduce((sum, item) => sum + pendingPhysicalSerials(item), 0);

  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <PageTitleWithHelp
          hoverHint={
            "Scan serialized device and box labels to pair them, adjust unserialized line quantities, and print the ZPL label when done. The planner assigns SKUs to the open carton number; if the queue changed, refresh or return to the Pre-Pack queue."
          }
          breadcrumbLabel={`Shipment ${String(carton.shipmentId)} · Carton ${String(carton.cartonNumber)}`}
        >
          <PrePackCartonShipmentTitle carton={carton} />
        </PageTitleWithHelp>
        <p className="mt-1 text-sm text-slate-500">
          <PrePackCartonBatchLink carton={carton} />
          {` · Ship by ${formatShipDate(carton.mfrShipByDate)}`}
        </p>
        <p className="mt-1 text-sm text-slate-500">
          {formatPackQty(totalDevicesPacked)} / {formatPackQty(totalDevicesPlanned)} devices packed
          {carton.sealed === true ? (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-800">
              <Lock className="h-3 w-3" aria-hidden />
              Sealed
            </span>
          ) : null}
          {cartonOverTargetCount(carton) > 0 ? (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-800">
              <AlertTriangle className="h-3 w-3" aria-hidden />
              {formatPackQty(cartonOverTargetCount(carton))} over target
            </span>
          ) : totalDevicesPending > 0 ? (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
              <Package className="h-3 w-3" aria-hidden />
              {formatPackQty(totalDevicesPending)} devices pending
            </span>
          ) : (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">
              <CheckCircle2 className="h-3 w-3" aria-hidden />
              Complete
            </span>
          )}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workspace body — plan summary, scan inputs, tables, print
// ---------------------------------------------------------------------------

interface CartonWorkspaceProps {
  carton: CartonRow;
  shippingWrite: boolean;
  laborSessionId?: number;
  onActionError: (message: string) => void;
  onAfterChange: () => void;
  onActivity: () => void;
  onRequestSeal: () => void;
  onRequestUnseal: () => void;
}

function CartonWorkspace({
  carton,
  shippingWrite,
  laborSessionId,
  onActionError,
  onAfterChange,
  onActivity,
  onRequestSeal,
  onRequestUnseal,
}: CartonWorkspaceProps) {
  const packingDisabled = carton.sealed === true;
  const serialsQuery = useQuery({
    queryKey: cartonSerialsKey(carton.shipmentId, carton.cartonNumber),
    queryFn: () => fetchCartonSerials(carton.shipmentId, carton.cartonNumber),
  });
  const unserializedQuery = useQuery({
    queryKey: cartonUnserializedKey(carton.shipmentId, carton.cartonNumber),
    queryFn: () => fetchCartonUnserialized(carton.shipmentId, carton.cartonNumber),
  });

  const {
    station,
    setStation,
    selectedPrinter: hubPrinterName,
    unavailableMessage,
    documents,
    printersQuery,
  } = usePrepackZplPrinter();
  const cartonPrinterName = hubPrinterName;
  const printStationId = Number(station);
  const boxStationId = Number.isInteger(printStationId) && printStationId > 0 ? printStationId : undefined;

  const [printBoxLabelInsteadOfScan, setPrintBoxLabelInsteadOfScan] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(PREPACK_PRINT_BOX_LABEL_STORAGE_KEY) === "true";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      PREPACK_PRINT_BOX_LABEL_STORAGE_KEY,
      printBoxLabelInsteadOfScan ? "true" : "false",
    );
  }, [printBoxLabelInsteadOfScan]);

  const [deviceLabel, setDeviceLabel] = useState("");
  const [boxScan, setBoxScan] = useState("");
  const deviceInputRef = useRef<HTMLInputElement>(null);
  const boxInputRef = useRef<HTMLInputElement>(null);
  const applyPrePackPageScanRef = useRef<(serial: string) => void>(() => {});
  useFloorPageScanCapture({
    inputRefs: [deviceInputRef, boxInputRef],
    onScan: (serial) => applyPrePackPageScanRef.current(serial),
  });
  const [scanError, setScanError] = useState<string | null>(null);
  const [printFailureNotice, setPrintFailureNotice] = useState<string | null>(null);

  function readDeviceLabelValue(): string {
    return readFloorSerialFromInput(deviceInputRef.current, deviceLabel);
  }

  function readBoxScanValue(): string {
    return readFloorSerialFromInput(boxInputRef.current, boxScan);
  }

  function submitBoxPairing(): void {
    const nextDevice = readDeviceLabelValue();
    const nextBox = readBoxScanValue();
    setDeviceLabel(nextDevice);
    setBoxScan(nextBox);
    if (nextDevice === "") {
      return;
    }
    if (!printBoxLabelInsteadOfScan && nextBox === "") {
      boxInputRef.current?.focus();
      return;
    }
    if (printBoxLabelInsteadOfScan && boxStationId == null) {
      setScanError("Pick a location before printing the box label.");
      return;
    }
    saveBoxLabelMutation.mutate();
  }

  const saveBoxLabelMutation = useMutation({
    mutationFn: () => {
      const nextDevice = readDeviceLabelValue();
      const nextBox = readBoxScanValue();
      return saveBoxLabel({
        shipmentId: carton.shipmentId,
        cartonNumber: carton.cartonNumber,
        deviceLabelSerial: nextDevice,
        ...(laborSessionId != null && laborSessionId > 0 ? { laborSessionId } : {}),
        ...(printBoxLabelInsteadOfScan
          ? { mode: "print" as const, stationId: boxStationId }
          : { mode: "scan" as const, boxScan: nextBox }),
      });
    },
    onSuccess: (result) => {
      setDeviceLabel("");
      setBoxScan("");
      setScanError(null);
      if (result.printed === false && result.printWarning != null && result.printWarning.trim() !== "") {
        playPairFailureSound();
        setPrintFailureNotice(result.printWarning);
      } else {
        playPairSuccessSound();
        setPrintFailureNotice(null);
      }
      onActivity();
      onAfterChange();
      requestAnimationFrame(() => deviceInputRef.current?.focus());
    },
    onError: (error) => {
      playPairFailureSound();
      setPrintFailureNotice(null);
      setScanError(error instanceof ApiError ? error.message : String(error));
    },
  });

  const removeBoxLabelMutation = useMutation({
    mutationFn: (serialFciId: number) => removeBoxLabel(serialFciId),
    onSuccess: () => {
      playPairSuccessSound();
      setPrintFailureNotice(null);
      onActivity();
      onAfterChange();
    },
    onError: (error) => {
      playPairFailureSound();
      onActionError(`Could not remove box label: ${error instanceof ApiError ? error.message : String(error)}`);
    },
  });

  const reprintBoxLabelMutation = useMutation({
    mutationFn: (serialFciId: number) => reprintBoxLabel(serialFciId, { stationId: boxStationId }),
    onSuccess: () => {
      setPrintFailureNotice(null);
      onActivity();
    },
    onError: (error) => {
      onActionError(error instanceof ApiError ? error.message : String(error));
    },
  });

  const packMutation = useMutation({
    mutationFn: ({ batchItemId, quantityPacked }: { batchItemId: number; quantityPacked: number }) =>
      packUnserialized({
        shipmentId: carton.shipmentId,
        cartonNumber: carton.cartonNumber,
        batchItemId,
        quantityPacked,
      }),
    onSuccess: () => {
      playPairSuccessSound();
      onActivity();
      onAfterChange();
    },
    onError: (error) => {
      playPairFailureSound();
      onActionError(`Could not pack line: ${error instanceof ApiError ? error.message : String(error)}`);
    },
  });

  const unpackMutation = useMutation({
    mutationFn: (batchItemId: number) =>
      unpackUnserialized(carton.shipmentId, carton.cartonNumber, batchItemId),
    onSuccess: () => {
      playPairSuccessSound();
      onActivity();
      onAfterChange();
    },
    onError: (error) => {
      playPairFailureSound();
      onActionError(`Could not unpack line: ${error instanceof ApiError ? error.message : String(error)}`);
    },
  });

  const printMutation = useMutation({
    mutationFn: (input: { printer: string; allowPending?: boolean }) =>
      printCartonLabel(carton.shipmentId, carton.cartonNumber, input.printer, {
        allowPending: input.allowPending,
      }),
    onSuccess: () => onActivity(),
    onError: (error) => {
      onActionError(error instanceof ApiError ? error.message : String(error));
    },
  });

  function handleDeviceKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    handleFloorSerialEnterKey(event, () => {
      if (printBoxLabelInsteadOfScan) {
        submitBoxPairing();
        return;
      }
      const nextDevice = readDeviceLabelValue();
      setDeviceLabel(nextDevice);
      if (nextDevice === "") {
        return;
      }
      boxInputRef.current?.focus();
    });
  }

  function handleBoxKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    handleFloorSerialEnterKey(event, submitBoxPairing);
  }

  applyPrePackPageScanRef.current = (serial) => {
    const active = document.activeElement;
    if (!printBoxLabelInsteadOfScan && active === boxInputRef.current) {
      setBoxScan(serial);
      if (boxInputRef.current != null) {
        boxInputRef.current.value = serial;
      }
      submitBoxPairing();
      return;
    }
    setDeviceLabel(serial);
    if (deviceInputRef.current != null) {
      deviceInputRef.current.value = serial;
    }
    if (printBoxLabelInsteadOfScan) {
      submitBoxPairing();
      return;
    }
    boxInputRef.current?.focus();
  };

  const [overrideOpen, setOverrideOpen] = useState(false);

  const serializedExpected = carton.items.filter((item) => item.isSerialized);
  const unserializedExpected = carton.items.filter((item) => !item.isSerialized);

  const packOutComponentsQuery = useQuery({
    queryKey: cartonPackOutComponentsKey(carton.shipmentId, carton.cartonNumber),
    queryFn: () => fetchPrePackCartonPackOutComponents(carton.shipmentId, carton.cartonNumber),
  });

  return (
    <>
    <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <div className="space-y-4">
        <Card>
          <CardBody className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Carton plan</p>
              {shippingWrite ? (
                <Button size="sm" variant="secondary" onClick={() => setOverrideOpen((open) => !open)}>
                  {overrideOpen ? "Close override" : "Override carton assignment"}
                </Button>
              ) : null}
            </div>
            <CartonPlanSummary items={carton.items} />
          </CardBody>
        </Card>
        <PackOutAccessoriesCard query={packOutComponentsQuery} />
      </div>

      <Card>
        <CardBody className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <h2 className="text-base font-semibold text-slate-900">Pack carton</h2>
              <HoverHintWithGlyph hoverHint={PACK_CARTON_SECTION_HOVER_HINT} />
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex flex-wrap items-center gap-2">
                {showSealCartonButton(carton) && shippingWrite ? (
                  <Button
                    size="sm"
                    variant="primary"
                    className="shrink-0"
                    onClick={onRequestSeal}
                    title="Mark this physical carton sealed and move remaining work to another carton."
                  >
                    <Lock className="mr-1.5 h-4 w-4" aria-hidden />
                    Seal carton
                  </Button>
                ) : null}
                {carton.sealed === true && shippingWrite ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="shrink-0"
                    onClick={onRequestUnseal}
                    title="Reopen this carton for packing. Does not undo item moves."
                  >
                    <Unlock className="mr-1.5 h-4 w-4" aria-hidden />
                    Unseal
                  </Button>
                ) : null}
                <ZplLocationSelect
                  id="prepack-zpl-location"
                  label="Printer"
                  size="compact"
                  showLabel={false}
                  value={station}
                  onChange={setStation}
                  unavailableMessage={unavailableMessage}
                
      documents={documents}
    />
                <Button
                  size="sm"
                  variant="secondary"
                  className="shrink-0"
                  onClick={() => printMutation.mutate({ printer: cartonPrinterName })}
                  disabled={printMutation.isPending || !carton.isFullyPacked}
                  title={
                    carton.isFullyPacked
                      ? undefined
                      : "Finish every line on the carton plan (no pending devices or quantities) before printing the carton label."
                  }
                >
                  {printMutation.isPending ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Printer className="mr-1.5 h-4 w-4" aria-hidden />
                  )}
                  Print carton label
                </Button>
                {shippingWrite ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="shrink-0"
                    onClick={() => printMutation.mutate({ printer: cartonPrinterName, allowPending: true })}
                    disabled={printMutation.isPending || cartonPrinterName.trim() === ""}
                    title="Print the carton label for what is already assigned, even if the plan still has pending lines."
                  >
                    Print assigned contents
                  </Button>
                ) : null}
              </div>
              <ZplPrinterListStatus printersQuery={printersQuery} />
            </div>
          </div>

          {packingDisabled ? (
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              This carton is sealed. Packed items stay here; remaining work was assigned to other cartons. Unseal to
              pack again (allocations are not moved back automatically).
            </p>
          ) : null}

          {serializedExpected.length > 0 && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <h3 className="text-sm font-semibold text-slate-900">Serialized devices</h3>
                <HoverHintWithGlyph hoverHint={SERIALIZED_DEVICES_SECTION_HOVER_HINT} />
              </div>
              <fieldset disabled={packingDisabled} className="space-y-2 disabled:opacity-60">
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <input
                  id="prepack-print-box-label-toggle"
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  checked={printBoxLabelInsteadOfScan}
                  onChange={(event) => {
                    setPrintBoxLabelInsteadOfScan(event.target.checked);
                    setBoxScan("");
                    setScanError(null);
                  }}
                />
                <Label htmlFor="prepack-print-box-label-toggle" className="cursor-pointer text-sm font-normal text-slate-800">
                  Print box label (skip box scan)
                </Label>
                <span className="text-xs text-slate-600">
                  Use scan when kitted labels remain; print on demand otherwise.
                </span>
              </div>
              <form
                className="space-y-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  submitBoxPairing();
                }}
              >
              <div className={`grid grid-cols-1 gap-2 ${printBoxLabelInsteadOfScan ? "" : "sm:grid-cols-2"}`}>
                <div>
                  <Label htmlFor="prepack-device-label">Device label</Label>
                  <Input
                    ref={deviceInputRef}
                    id="prepack-device-label"
                    name="device_label"
                    value={deviceLabel}
                    onChange={(event) => {
                      const raw = event.target.value;
                      if (floorSerialInputHasScannerTerminator(raw)) {
                        const nextDevice = normalizeFloorSerialInput(raw);
                        setDeviceLabel(nextDevice);
                        if (nextDevice === "") {
                          return;
                        }
                        if (printBoxLabelInsteadOfScan) {
                          submitBoxPairing();
                          return;
                        }
                        boxInputRef.current?.focus();
                        return;
                      }
                      setDeviceLabel(raw);
                    }}
                    onKeyDown={handleDeviceKeyDown}
                    placeholder="Scan device label"
                    autoFocus
                    {...FLOOR_SCAN_INPUT_FIELD_PROPS}
                    {...FLOOR_PAGE_SCAN_TARGET_PROPS}
                    className={cn(FLOOR_SCAN_INPUT_FIELD_PROPS.className, "mt-1")}
                  />
                </div>
                {printBoxLabelInsteadOfScan ? null : (
                  <div>
                    <Label htmlFor="prepack-box-scan">Box label</Label>
                    <Input
                      ref={boxInputRef}
                      id="prepack-box-scan"
                      name="box_label"
                      value={boxScan}
                      onChange={(event) => {
                        const raw = event.target.value;
                        if (floorSerialInputHasScannerTerminator(raw)) {
                          setBoxScan(normalizeFloorSerialInput(raw));
                          submitBoxPairing();
                          return;
                        }
                        setBoxScan(raw);
                      }}
                      onKeyDown={handleBoxKeyDown}
                      placeholder="Scan box label"
                      {...FLOOR_SCAN_INPUT_FIELD_PROPS}
                      {...FLOOR_PAGE_SCAN_TARGET_PROPS}
                      className={cn(FLOOR_SCAN_INPUT_FIELD_PROPS.className, "mt-1")}
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  className="min-h-12 px-6 text-base"
                  disabled={
                    saveBoxLabelMutation.isPending ||
                    deviceLabel.trim() === "" ||
                    (!printBoxLabelInsteadOfScan && boxScan.trim() === "")
                  }
                >
                  {saveBoxLabelMutation.isPending ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <ScanLine className="mr-1.5 h-4 w-4" aria-hidden />
                  )}
                  {printBoxLabelInsteadOfScan ? "Save pairing and print" : "Save pairing"}
                </Button>
              </div>
              </form>
              {scanError ? (
                <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                  {scanError}
                </p>
              ) : null}
              {printFailureNotice ? (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>{printFailureNotice}</span>
                  <button
                    type="button"
                    className="ml-auto shrink-0 text-amber-800 hover:text-amber-950"
                    onClick={() => setPrintFailureNotice(null)}
                    aria-label="Dismiss print warning"
                  >
                    <XCircle className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              ) : null}
              </fieldset>

              <SerialsTable
                query={serialsQuery}
                onRemove={(serialFciId) => removeBoxLabelMutation.mutate(serialFciId)}
                isRemoving={removeBoxLabelMutation.isPending}
                onReprint={(serialFciId) => reprintBoxLabelMutation.mutate(serialFciId)}
                reprintingSerialFciId={
                  reprintBoxLabelMutation.isPending && reprintBoxLabelMutation.variables != null
                    ? reprintBoxLabelMutation.variables
                    : null
                }
              />
            </div>
          )}

          {unserializedExpected.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-900">Unserialized lines</h3>
              <UnserializedTable
                query={unserializedQuery}
                onPack={(batchItemId, qty) => packMutation.mutate({ batchItemId, quantityPacked: qty })}
                onUnpack={(batchItemId) => unpackMutation.mutate(batchItemId)}
                isMutating={packMutation.isPending || unpackMutation.isPending}
                packingDisabled={packingDisabled}
              />
            </div>
          )}
        </CardBody>
      </Card>
    </div>
    <PrePackCartonTargetModal
      open={overrideOpen}
      shipmentId={carton.shipmentId}
      onClose={() => setOverrideOpen(false)}
      onSaved={() => {
        onActivity();
        onAfterChange();
      }}
    />
    </>
  );
}

// ---------------------------------------------------------------------------
// Pack Out accessories (BOM)
// ---------------------------------------------------------------------------

function shouldShowPackOutAccessoriesPanel(groups: PackOutComponentsResponse["groups"]): boolean {
  if (groups.length === 0) return false;
  return groups.some((group) => !group.has_template || group.components.length > 0);
}

function packOutAccessoryUnitLabel(group: PackOutComponentsResponse["groups"][number], quantity: number): string {
  if (group.kit_divisor != null && group.kit_divisor > 1) {
    return quantity === 1 ? "kit" : "kits";
  }
  return quantity === 1 ? "unit" : "units";
}

function packOutAccessoryPerColumnLabel(group: PackOutComponentsResponse["groups"][number]): string {
  return group.kit_divisor != null && group.kit_divisor > 1 ? "Per kit" : "Per unit";
}

function packOutComponentSkuHoverHint(component: PackOutComponent): JSX.Element {
  return <EntityPeekHint kind="sku_metadata" fields={component} />;
}

interface PackOutAccessoriesCardProps {
  query: ReturnType<typeof useQuery<PackOutComponentsResponse, Error>>;
}

function PackOutAccessoriesCard({ query }: PackOutAccessoriesCardProps) {
  if (query.isLoading) {
    return (
      <Card>
        <CardBody className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading Pack Out accessories…
        </CardBody>
      </Card>
    );
  }

  if (query.isError) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-amber-800">
            {query.error instanceof ApiError
              ? query.error.message
              : "Could not load Pack Out accessories for this carton."}
          </p>
        </CardBody>
      </Card>
    );
  }

  const data = query.data;
  if (!data || !shouldShowPackOutAccessoriesPanel(data.groups)) {
    return null;
  }

  const stationLabel = data.station.name.trim() !== "" ? data.station.name : "Pack Out";

  return (
    <Card>
      <CardBody className="space-y-4">
        <CardTitleWithHelp hoverHint={PACK_OUT_ACCESSORIES_HOVER_HINT}>
          {`${stationLabel} accessories`}
        </CardTitleWithHelp>
        {data.groups.map((group) => (
          <div key={group.finished_sku} className="space-y-2 border-t border-slate-200 pt-3 first:border-t-0 first:pt-0">
            <p className="text-sm font-semibold text-slate-900">
              {group.finished_sku}{" "}
              <span className="font-normal text-slate-600">
                × {formatPackQty(group.planned_units)} {packOutAccessoryUnitLabel(group, group.planned_units)}
              </span>
            </p>
            {!group.has_template ? (
              <p className="text-xs text-slate-500">No BOM template assigned to this SKU.</p>
            ) : group.components.length === 0 ? (
              <p className="text-xs text-slate-500">No Pack Out accessories defined in BOM.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-slate-500">
                  <tr>
                    <th className="py-1 text-left font-medium">Accessory</th>
                    <th className="py-1 text-left font-medium">SKU</th>
                    <th className="py-1 text-right font-medium">{packOutAccessoryPerColumnLabel(group)}</th>
                    <th className="py-1 text-right font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {group.components.map((component) => (
                    <tr
                      key={`${group.finished_sku}-${component.component_sku}-${component.variable_name ?? ""}`}
                      className="border-t border-slate-200"
                    >
                      <td className="py-1 font-sans text-slate-800">
                        {component.variable_name?.trim() !== "" && component.variable_name != null
                          ? component.variable_name
                          : component.component_sku}
                      </td>
                      <td className="py-1 text-slate-700">
                        <HoverHintContent hoverHint={packOutComponentSkuHoverHint(component)}>
                          <span>{component.component_sku}</span>
                        </HoverHintContent>
                      </td>
                      <td className="py-1 text-right font-sans text-slate-800">
                        {formatPackQty(component.quantity_per_unit)}
                      </td>
                      <td className="py-1 text-right font-sans font-medium text-slate-900">
                        {formatPackQty(component.quantity_total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Carton plan summary
// ---------------------------------------------------------------------------

function CartonPlanSummary({ items }: { items: CartonItem[] }) {
  const hasKitSku = items.some((item) => item.kitDivisor !== undefined && item.kitDivisor > 1);
  return (
    <div>
      {hasKitSku ? (
        <div className="mb-2">
          <HoverHintWithGlyph hoverHint={CARTON_PLAN_KIT_SKU_HOVER_HINT} />
        </div>
      ) : null}
      <table className="w-full text-sm">
        <thead className="text-slate-500">
          <tr>
            <th className="py-1 text-left font-medium">SKU</th>
            <th className="py-1 text-right font-medium">Planned</th>
            <th className="py-1 text-right font-medium">Packed</th>
            <th className="py-1 text-right font-medium">Pending</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {items.map((item, idx) => (
            <tr key={`${item.sku}-${item.source}-${idx}`} className="border-t border-slate-200">
              <td className="py-1">
                {item.sku}
                {item.isSerialized ? null : (
                  <span className="ml-1 inline-block whitespace-nowrap rounded bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-600">
                    Unserialized
                  </span>
                )}
              </td>
              <td className="py-1 text-right align-top font-sans text-xs text-slate-800">
                {item.kitDivisor !== undefined && item.kitDivisor > 1 ? (
                  formatKitSerialBreakdown(plannedPhysicalSerials(item), item.kitDivisor)
                ) : (
                  formatPackQty(item.plannedQty)
                )}
              </td>
              <td className="py-1 text-right align-top font-sans text-xs text-slate-800">
                {item.kitDivisor !== undefined && item.kitDivisor > 1 ? (
                  formatKitSerialBreakdown(packedPhysicalSerials(item), item.kitDivisor)
                ) : (
                  formatPackQty(item.packedQty)
                )}
              </td>
              <td className="py-1 text-right align-top font-sans text-xs">
                {item.pending > 0 ? (
                  <span className="text-amber-700">
                    {item.kitDivisor !== undefined && item.kitDivisor > 1 ? (
                      formatKitSerialBreakdown(pendingPhysicalSerials(item), item.kitDivisor)
                    ) : (
                      formatPackQty(item.pending)
                    )}
                  </span>
                ) : (
                  <span className="text-emerald-700">0</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Serials table — devices boxed for the current carton (DB rows)
// ---------------------------------------------------------------------------

interface SerialsTableProps {
  query: ReturnType<typeof useQuery<CartonSerialRow[], Error>>;
  onRemove: (serialFciId: number) => void;
  isRemoving: boolean;
  onReprint: (serialFciId: number) => void;
  reprintingSerialFciId: number | null;
}

function SerialsTable({ query, onRemove, isRemoving, onReprint, reprintingSerialFciId }: SerialsTableProps) {
  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-slate-500">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        Loading boxed devices…
      </div>
    );
  }
  if (query.isError) {
    return (
      <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
        Could not load boxed devices: {query.error instanceof Error ? query.error.message : "unknown"}
      </p>
    );
  }
  const rows = query.data ?? [];
  if (rows.length === 0) {
    return <p className="py-2 text-xs text-slate-500">No boxed devices yet.</p>;
  }
  return (
    <table className="w-full text-xs">
      <thead className="text-slate-500">
        <tr>
          <th className="py-1 text-left font-medium">SKU</th>
          <th className="py-1 text-left font-medium">Device label</th>
          <th className="py-1 text-left font-medium">Box label</th>
          <th className="py-1" />
        </tr>
      </thead>
      <tbody className="font-mono">
        {rows.map((row) => (
          <tr key={row.id} className="border-t border-slate-200">
            <td className="py-1">{row.finished_sku ?? "—"}</td>
            <td className="py-1">{row.device_label_serial ?? "—"}</td>
            <td className="py-1">{row.box_serial ?? "—"}</td>
            <td className="py-1 text-right">
              <div className="flex justify-end gap-1">
                {row.label_printed_serial_id != null ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onReprint(row.id)}
                    disabled={isRemoving || reprintingSerialFciId != null}
                    aria-label={`Reprint box label for ${row.device_label_serial ?? "device"}`}
                    title="Reprint hub box label"
                  >
                    {reprintingSerialFciId === row.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                    ) : (
                      <Printer className="h-3 w-3" aria-hidden />
                    )}
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onRemove(row.id)}
                  disabled={isRemoving || reprintingSerialFciId != null}
                  aria-label={`Remove box label for ${row.device_label_serial ?? "device"}`}
                >
                  <Trash2 className="h-3 w-3" aria-hidden />
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Unserialized table — pack/unpack quantities
// ---------------------------------------------------------------------------

interface UnserializedTableProps {
  query: ReturnType<typeof useQuery<CartonUnserializedItem[], Error>>;
  onPack: (batchItemId: number, quantityPacked: number) => void;
  onUnpack: (batchItemId: number) => void;
  isMutating: boolean;
  packingDisabled?: boolean;
}

function UnserializedTable({
  query,
  onPack,
  onUnpack,
  isMutating,
  packingDisabled = false,
}: UnserializedTableProps) {
  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-slate-500">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        Loading unserialized lines…
      </div>
    );
  }
  if (query.isError) {
    return (
      <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
        Could not load unserialized lines: {query.error instanceof Error ? query.error.message : "unknown"}
      </p>
    );
  }
  const rows = query.data ?? [];
  if (rows.length === 0) {
    return <p className="py-2 text-xs text-slate-500">No unserialized lines on the plan here.</p>;
  }
  return (
    <table className="w-full text-sm">
      <thead className="text-slate-500">
        <tr>
          <th className="py-1 text-left font-medium">SKU</th>
          <th className="py-1 text-right font-medium">Order qty</th>
          <th className="py-1 text-right font-medium">Packed qty</th>
          <th className="py-1 text-right font-medium">Pending</th>
          <th className="py-1" />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <UnserializedRowItem
            key={row.batchItemId}
            row={row}
            onPack={onPack}
            onUnpack={onUnpack}
            disabled={isMutating || packingDisabled}
          />
        ))}
      </tbody>
    </table>
  );
}

interface UnserializedRowItemProps {
  row: CartonUnserializedItem;
  onPack: (batchItemId: number, quantityPacked: number) => void;
  onUnpack: (batchItemId: number) => void;
  disabled: boolean;
}

function UnserializedRowItem({ row, onPack, onUnpack, disabled }: UnserializedRowItemProps) {
  const [draft, setDraft] = useState<string>(row.quantityPacked > 0 ? String(row.quantityPacked) : "");

  useEffect(() => {
    setDraft(row.quantityPacked > 0 ? String(row.quantityPacked) : "");
  }, [row.quantityPacked]);

  const isComplete = row.pending === 0 && row.quantityPacked > 0;

  return (
    <tr className="border-t border-slate-200">
      <td className="py-1 font-mono">{row.sku}</td>
      <td className="py-1 text-right font-mono">{row.shipmentQuantity}</td>
      <td className="py-1 text-right">
        <Input
          type="number"
          min={0}
          max={row.shipmentQuantity}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className="ml-auto h-8 w-24 text-right text-sm"
          disabled={disabled}
        />
      </td>
      <td className="py-1 text-right font-mono">
        {row.pending > 0 ? (
          <span className="text-amber-700">{formatPackQty(row.pending)}</span>
        ) : (
          <span className="text-emerald-700">0</span>
        )}
      </td>
      <td className="py-1 text-right">
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="secondary"
            disabled={disabled || draft.trim() === "" || Number(draft) === row.quantityPacked}
            onClick={() => {
              const qty = Number(draft);
              if (!Number.isFinite(qty) || qty < 0) return;
              if (qty > row.shipmentQuantity) return;
              onPack(row.batchItemId, qty);
            }}
          >
            Save
          </Button>
          {isComplete ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onUnpack(row.batchItemId)}
              disabled={disabled}
              aria-label={`Unpack ${row.sku}`}
            >
              <Trash2 className="h-3 w-3" aria-hidden />
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
